import { Turbo } from '@hotwired/turbo-rails'
import { Modal } from 'bootstrap'

// Disable Turbo Drive for all forms globally until each form is audited.
// Individual forms can opt in with data-turbo="true".
Turbo.config.forms.mode = 'optin'

// Turbo Drive replaces <body> and merges <head> but does NOT update <html>
// attributes. Sync them from the server response so that data-bs-color-scheme
// (and lang, etc.) are kept up to date across navigations.
document.addEventListener('turbo:before-render', (event) => {
  const newHtml = event.detail.newBody.parentElement
  if (newHtml) {
    for (const attr of newHtml.attributes) {
      document.documentElement.setAttribute(attr.name, attr.value)
    }
  }
})

// Page transitions by the kind of link, see "Page transitions" in layout.sass.
// A click hands the kind its nav names to the visit it starts, which carries
// it on <html> from turbo:visit to turbo:load, as Turbo does with its own
// data-turbo-visit-direction. A form submitted through Turbo, such as the
// filter search, hands on the kind of what it sits in the same way. Visits no
// link or form starts, back and forward among them, carry none. A link to the
// next or previous page of a list says so itself, with rel, as Pagy's do.
let startedVisitTransition

const pageTransitionOf = (link) => {
  if (link.relList.contains('next')) return 'next'
  if (link.relList.contains('prev')) return 'previous'
  return link.closest('[data-page-transition]')?.dataset.pageTransition
}

document.addEventListener('turbo:click', (event) => {
  startedVisitTransition = pageTransitionOf(event.target)
})

document.addEventListener('turbo:submit-start', (event) => {
  startedVisitTransition = event.target.closest('[data-page-transition]')?.dataset.pageTransition
})

document.addEventListener('turbo:visit', () => {
  if (startedVisitTransition) {
    document.documentElement.dataset.visitTransition = startedVisitTransition
  } else {
    delete document.documentElement.dataset.visitTransition
  }
  startedVisitTransition = undefined
})

document.addEventListener('turbo:load', () => {
  delete document.documentElement.dataset.visitTransition
})

// #page_sidenav is data-turbo-permanent: Turbo carries the current element over
// to the new page and drops the new page's copy. Morph that copy's contents into
// the kept element first, so the active item and the links that depend on the
// user's open records match the new page. The element's own attributes stay
// as they are, Bootstrap's offcanvas keeps its state there.
//
// Carrying it over takes the element out of the document for a moment. Chrome
// keeps its scroll position through that, Safari and Firefox reset it to the
// top, so it is put back by hand once the new page is in.
let sidenavScrollTops = null

document.addEventListener('turbo:before-render', (event) => {
  const currentSidenav = document.getElementById('page_sidenav')
  const newSidenav = event.detail.newBody.querySelector('#page_sidenav')
  if (!currentSidenav || !newSidenav) return

  sidenavScrollTops = sidenavScrollers().map((scroller) => scroller.scrollTop)
  Turbo.morphChildren(currentSidenav, newSidenav)
})

document.addEventListener('turbo:render', () => {
  if (!sidenavScrollTops) return

  sidenavScrollers().forEach((scroller, idx) => { scroller.scrollTop = sidenavScrollTops[idx] })
  sidenavScrollTops = null
})

// The sidenav scrolls itself on desktop and its body as an offcanvas
function sidenavScrollers() {
  const sidenav = document.getElementById('page_sidenav')
  return sidenav ? [sidenav, sidenav.querySelector('.offcanvas-body')] : []
}

// Custom confirm dialog — replaces jquery-ujs $.rails.allowAction.
// Reads data-ok, data-cancel, data-hint from the triggering element. The hint
// is plain text or, from an `_html` translation, paragraphs of markup.
//
// For data-turbo-method links, Turbo synthesises a <form> and only copies
// data-turbo-confirm onto it — not data-ok/cancel/hint. So we fall back to
// finding the originating <a> by matching the confirm message.
Turbo.config.forms.confirm = (message, element, submitter) => {
  return new Promise((resolve) => {
    const source = submitter ||
      (element.tagName === 'FORM'
        ? document.querySelector(`a[data-turbo-confirm="${CSS.escape(message)}"]`) || element
        : element)

    const okText = source.dataset.ok || 'OK'
    const cancelText = source.dataset.cancel || 'Cancel'
    const hintText = source.dataset.hint || ''

    // Remove any existing modal to avoid stacking
    const existing = document.getElementById('confirmation_modal')
    if (existing) existing.remove()

    const wrapper = document.createElement('div')
    wrapper.innerHTML = `
      <div id="confirmation_modal" class="modal fade" tabindex="-1" role="dialog"
           aria-labelledby="confirmation_modal" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered">
          <div class="modal-content">
            <div class="modal-header">
              <h4 class="modal-title">${message}</h4>
              <button type="button" class="btn-close" data-bs-dismiss="modal"
                      aria-label="Close"></button>
            </div>
            <div class="modal-body">${hintText}</div>
            <div class="modal-footer">
              <button class="btn btn-outline-dark" data-bs-dismiss="modal"
                      aria-hidden="true">${cancelText}</button>
              <button class="btn btn-danger confirm">${okText}</button>
            </div>
          </div>
        </div>
      </div>`.trim()
    const modalEl = wrapper.firstChild
    document.body.appendChild(modalEl)

    const modal = new Modal(modalEl)

    modalEl.querySelector('.confirm').addEventListener('click', () => {
      modal.hide()
      resolve(true)
    })

    modalEl.addEventListener('hidden.bs.modal', () => {
      modalEl.remove()
      resolve(false)
    })

    modalEl.addEventListener('shown.bs.modal', () => {
      modalEl.querySelector('.confirm').focus()
    })

    modal.show()
  })
}
